import { useEffect, useState } from "react";
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
    ModalCloseButton, Button, VStack, HStack, Text, Box, Spinner,
    Alert, AlertIcon, RadioGroup, Radio, Stack, Select, Badge, useToast,
} from "@chakra-ui/react";
import { FiCheck, FiX, FiSlash } from "react-icons/fi";
import {
    getListasAbertasFornecedor,
    adicionarItemVendaEmLista,
    marcarIgnorarListaCompras,
    desmarcarIgnorarListaCompras,
} from "../../services/api";

const STATUS_LABEL = {
    pendente: "Pendente",
    em_andamento: "Em Andamento",
};

export default function AdicionarItemListaModal({
    isOpen,
    onClose,
    itemVendaId,
    fornecedorId,
    fornecedorNome,
    produtoNome,
    statusAtual,
    onUpdated,
}) {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [listas, setListas] = useState([]);
    const [modo, setModo] = useState("adicionar");
    const [listaSelecionada, setListaSelecionada] = useState("");

    const jaIgnorado = statusAtual === "ignorado";

    useEffect(() => {
        if (!isOpen || !fornecedorId) return;
        let cancelado = false;
        (async () => {
            setLoading(true);
            const data = await getListasAbertasFornecedor(fornecedorId);
            if (cancelado) return;
            const arr = (data && Array.isArray(data.listas)) ? data.listas : [];
            setListas(arr);
            setListaSelecionada(arr.length > 0 ? String(arr[0].id) : "");
            setModo(jaIgnorado ? "desfazer_ignorar" : (arr.length > 0 ? "adicionar" : "ignorar"));
            setLoading(false);
        })();
        return () => { cancelado = true; };
    }, [isOpen, fornecedorId, jaIgnorado]);

    const formatarData = (dataString) => {
        if (!dataString) return "-";
        return new Date(dataString).toLocaleDateString("pt-BR");
    };

    const confirmarAdicionar = async () => {
        if (!listaSelecionada) {
            toast({ title: "Selecione uma lista", status: "warning", duration: 2500 });
            return;
        }
        setSaving(true);
        const r = await adicionarItemVendaEmLista(Number(listaSelecionada), itemVendaId);
        setSaving(false);
        if (r && r.item_lista_id) {
            toast({ title: "Item adicionado à lista", status: "success", duration: 2500 });
            onUpdated?.({
                status: "na_lista",
                lista_id: r.lista_id,
                item_lista_id: r.item_lista_id,
            });
            onClose();
        } else {
            const msg = r?.message || "Erro ao adicionar item à lista";
            toast({ title: msg, status: "error", duration: 4000 });
        }
    };

    const confirmarIgnorar = async () => {
        setSaving(true);
        const r = await marcarIgnorarListaCompras(itemVendaId);
        setSaving(false);
        if (r) {
            toast({ title: "Item marcado para nunca entrar em listas", status: "success", duration: 2500 });
            onUpdated?.({ status: "ignorado", lista_id: null, item_lista_id: null });
            onClose();
        } else {
            toast({ title: "Erro ao marcar item", status: "error", duration: 4000 });
        }
    };

    const confirmarDesfazerIgnorar = async () => {
        setSaving(true);
        const r = await desmarcarIgnorarListaCompras(itemVendaId);
        setSaving(false);
        if (r) {
            toast({ title: "Item voltou a ser elegível para listas", status: "success", duration: 2500 });
            onUpdated?.({ status: null, lista_id: null, item_lista_id: null });
            onClose();
        } else {
            toast({ title: "Erro ao reverter", status: "error", duration: 4000 });
        }
    };

    const confirmar = () => {
        if (modo === "adicionar") return confirmarAdicionar();
        if (modo === "ignorar") return confirmarIgnorar();
        if (modo === "desfazer_ignorar") return confirmarDesfazerIgnorar();
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader color="#01695E">
                    Lista de compras — item de venda #{itemVendaId}
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack align="stretch" spacing={4}>
                        <Box bg="gray.50" borderRadius="md" p={3}>
                            <Text fontSize="sm" color="gray.600">Produto</Text>
                            <Text fontWeight="bold" fontSize="sm">{produtoNome || "—"}</Text>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                                Fornecedor: <b>{fornecedorNome || `#${fornecedorId}`}</b>
                            </Text>
                        </Box>

                        {loading ? (
                            <HStack justify="center" py={6}>
                                <Spinner color="#F68633" />
                                <Text fontSize="sm" color="gray.600">Carregando listas abertas…</Text>
                            </HStack>
                        ) : (
                            <RadioGroup value={modo} onChange={setModo}>
                                <Stack spacing={3}>
                                    {jaIgnorado ? (
                                        <Box borderWidth="1px" borderRadius="md" p={3} borderColor="gray.300">
                                            <Radio value="desfazer_ignorar" colorScheme="green">
                                                <Text fontWeight="bold">Voltar a considerar este item</Text>
                                            </Radio>
                                            <Text fontSize="xs" color="gray.600" ml={6}>
                                                Reverte a marcação "ignorar para sempre". O item volta a ser elegível na próxima geração automática de listas.
                                            </Text>
                                        </Box>
                                    ) : (
                                        <>
                                            <Box borderWidth="1px" borderRadius="md" p={3} borderColor={modo === "adicionar" ? "#01695E" : "gray.200"}>
                                                <Radio value="adicionar" colorScheme="green" isDisabled={listas.length === 0}>
                                                    <Text fontWeight="bold">Adicionar a uma lista existente do fornecedor</Text>
                                                </Radio>
                                                {listas.length === 0 ? (
                                                    <Alert status="info" mt={2} fontSize="sm" borderRadius="md">
                                                        <AlertIcon />
                                                        Nenhuma lista aberta (pendente/em andamento) para este fornecedor.
                                                    </Alert>
                                                ) : (
                                                    <Box mt={2} ml={6}>
                                                        <Select
                                                            size="sm"
                                                            value={listaSelecionada}
                                                            onChange={(e) => setListaSelecionada(e.target.value)}
                                                            isDisabled={modo !== "adicionar"}
                                                        >
                                                            {listas.map((l) => (
                                                                <option key={l.id} value={l.id}>
                                                                    Lista #{l.id} — {formatarData(l.data_lista)} ({STATUS_LABEL[l.status_resposta] || l.status_resposta}) — {l.quantidade_itens_respondidos}/{l.quantidade_itens}
                                                                </option>
                                                            ))}
                                                        </Select>
                                                    </Box>
                                                )}
                                            </Box>

                                            <Box borderWidth="1px" borderRadius="md" p={3} borderColor={modo === "ignorar" ? "#F68633" : "gray.200"}>
                                                <Radio value="ignorar" colorScheme="orange">
                                                    <HStack>
                                                        <Text fontWeight="bold">Não entrar em nenhuma lista pra sempre</Text>
                                                        <Badge colorScheme="orange">irreversível por engano: reabra este modal para desfazer</Badge>
                                                    </HStack>
                                                </Radio>
                                                <Text fontSize="xs" color="gray.600" ml={6} mt={1}>
                                                    Marca este item de venda específico para ser ignorado pela geração automática de listas. Não afeta outras vendas do mesmo produto.
                                                </Text>
                                            </Box>
                                        </>
                                    )}
                                </Stack>
                            </RadioGroup>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={onClose} leftIcon={<FiX />}>
                        Cancelar
                    </Button>
                    <Button
                        colorScheme={modo === "ignorar" ? "orange" : "green"}
                        bg={modo === "ignorar" ? "#F68633" : "#01695E"}
                        onClick={confirmar}
                        isLoading={saving}
                        isDisabled={loading || (modo === "adicionar" && !listaSelecionada)}
                        leftIcon={modo === "ignorar" ? <FiSlash /> : <FiCheck />}
                    >
                        {modo === "ignorar" && "Ignorar pra sempre"}
                        {modo === "adicionar" && "Adicionar à lista"}
                        {modo === "desfazer_ignorar" && "Voltar a considerar"}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
